import React from 'react';
import type { LucideIcon } from 'lucide-react';

export interface ChoiceOption<TValue extends string> {
  readonly value: TValue;
  readonly label: string;
  readonly description?: string;
  readonly icon?: LucideIcon;
  readonly disabled?: boolean;
}

export interface SegmentedChoiceProps<TValue extends string> {
  label: string;
  options: readonly ChoiceOption<TValue>[];
  value: TValue | null;
  onChange: (value: TValue) => void;
  /** Grid columns; 1 renders a stacked list of rich options. */
  columns?: number;
  hint?: string;
}

/**
 * Pill selector. With one column each option can carry a description, which is
 * how the load and rail pickers explain what a choice means.
 */
export function SegmentedChoice<TValue extends string>({
  label,
  options,
  value,
  onChange,
  columns = 2,
  hint,
}: SegmentedChoiceProps<TValue>): React.ReactElement {
  const gridClass = columns === 1 ? 'grid-cols-1' : 'grid-cols-2';

  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-[#333333]">{label}</span>
      <div className={`grid gap-2 ${gridClass}`} role="radiogroup" aria-label={label}>
        {options.map((option) => {
          const isSelected = option.value === value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={option.disabled ?? false}
              onClick={() => onChange(option.value)}
              className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
                isSelected
                  ? 'border-[#D4A054] bg-[#FDF6E9]'
                  : 'border-[#E0E0E0] bg-white hover:border-[#D4A054]/60'
              }`}
            >
              {Icon && (
                <Icon
                  className={`mt-0.5 h-5 w-5 shrink-0 ${isSelected ? 'text-[#D4A054]' : 'text-[#666666]'}`}
                />
              )}
              <span className="min-w-0">
                <span className="block text-sm font-medium text-[#333333]">{option.label}</span>
                {option.description && (
                  <span className="mt-0.5 block text-xs text-[#666666]">{option.description}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {hint && <span className="mt-1.5 block text-xs text-[#666666]">{hint}</span>}
    </div>
  );
}

export default SegmentedChoice;
